import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { asset } from '@/lib/api';
import { SectionHeading } from '@/components/SectionHeading';
import { usePrefersReducedMotion } from '@/lib/hooks';
import { MagneticButton } from '@/components/MagneticButton';
import { EquipImage } from '@/components/EquipImage';

const Card = ({ item }) => (
  <div className='min-w-0'>
    <Link to='/equipment' data-cursor='VIEW' data-testid={`fleet-card-${item.slug}`} className='group block'>
      <div className='relative h-64 overflow-hidden rounded-2xl border border-white/10 sm:h-72'>
        <EquipImage item={item} />
        <div className='absolute inset-0' style={{ background: 'linear-gradient(180deg, transparent 40%, rgba(32,39,42,0.9) 100%)' }} />
        <span className='absolute bottom-4 left-4 right-4'>
          <span className='block h-px w-0 bg-[#B88A3B] transition-all duration-500 group-hover:w-14' />
          <span className='mt-3 block text-[11px] uppercase tracking-wider text-[#B88A3B]'>{item.category}</span>
          <span className='font-head text-xl font-semibold text-[#F6F5F1]'>{item.name}</span>
        </span>
      </div>
    </Link>
  </div>
);

export const EquipmentExperience = ({ equipment = [] }) => {
  const sectionRef = useRef(null);
  const reduced = usePrefersReducedMotion();
  // Single shared scroll progress for the whole section so both rows stay in sync.
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  // Row 1 drifts left -> right; Row 2 drifts right -> left, each as one solid group.
  const xLtr = useTransform(scrollYProgress, [0, 1], [-72, 72]);
  const xRtl = useTransform(scrollYProgress, [0, 1], [72, -72]);

  const list = equipment.slice(0, 6);
  if (!list.length) return null;
  const row1 = list.slice(0, 3);
  const row2 = list.slice(3, 6);

  return (
    <section ref={sectionRef} className='relative overflow-hidden bg-[#171d1f]' data-testid='home-fleet-section'>
      <div className='grain absolute inset-0' />
      <div className='relative container-x py-20 lg:py-28'>
        <div className='flex flex-col justify-between gap-6 md:flex-row md:items-end'>
          <SectionHeading eyebrow='Powered by the Right Machine' title='The fleet behind the work.' light />
          <MagneticButton to='/equipment' variant='secondary' className='shrink-0 text-[#F6F5F1]' data-cursor='VIEW'>
            Explore Equipment
          </MagneticButton>
        </div>

        <div className='mt-14 space-y-6'>
          <motion.div
            style={reduced ? {} : { x: xLtr }}
            className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'
            data-testid='fleet-row-ltr'
          >
            {row1.map((e) => (
              <Card key={e.slug} item={e} />
            ))}
          </motion.div>

          {row2.length > 0 && (
            <motion.div
              style={reduced ? {} : { x: xRtl }}
              className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'
              data-testid='fleet-row-rtl'
            >
              {row2.map((e) => (
                <Card key={e.slug} item={e} />
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};
